// Mirrors backend/mentions.py. A mention is stored inside the text as
// `@[Display Name](kind:id)` so the label survives renames and the id is what
// links and notifies.

export const MENTION_PATTERN = /@\[([^\]\n]{1,80})\]\((user|student|agency):([A-Za-z0-9_:-]{1,64})\)/g;

export const MENTION_KINDS = ['user', 'student', 'agency'];

export function mentionToken(kind, id, label) {
  // Brackets in a name would break the token, so they are dropped rather than
  // escaped -- the label is display-only and the id carries the meaning.
  const safeLabel = String(label || '').replace(/[[\]()\n]/g, '').trim() || 'Unknown';
  return `@[${safeLabel}](${kind}:${id})`;
}

export function parseMentions(text) {
  const found = [];
  const seen = new Set();
  const pattern = new RegExp(MENTION_PATTERN.source, 'g');
  let match = pattern.exec(String(text || ''));
  while (match) {
    const key = `${match[2]}:${match[3]}`;
    if (!seen.has(key)) {
      seen.add(key);
      found.push({ label: match[1], kind: match[2], id: match[3] });
    }
    match = pattern.exec(String(text || ''));
  }
  return found;
}

/* Splits text into plain strings and mention objects, ready to render. */
export function splitMentions(text) {
  const source = String(text || '');
  const parts = [];
  const pattern = new RegExp(MENTION_PATTERN.source, 'g');
  let lastIndex = 0;
  let match = pattern.exec(source);
  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: source.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'mention', label: match[1], kind: match[2], id: match[3] });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(source);
  }
  if (lastIndex < source.length) {
    parts.push({ type: 'text', value: source.slice(lastIndex) });
  }
  return parts;
}

export function isUserMentioned(mentions, userId) {
  const target = String(userId || '');
  if (!target) return false;
  return (Array.isArray(mentions) ? mentions : []).some(
    (mention) => mention?.kind === 'user' && String(mention?.id) === target,
  );
}

export function mentionProfileLink(mention) {
  if (mention?.kind === 'student') return `/admin/students/${mention.id}`;
  if (mention?.kind === 'agency') return `/admin/agencies/${mention.id}`;
  return '';
}

/* Finds the `@word` the caret is currently inside, so the picker knows what to
   search for and what to replace. Returns null when the caret is not in one. */
export function activeMentionQuery(text, caret) {
  const source = String(text || '');
  const position = Number.isFinite(caret) ? caret : source.length;
  const before = source.slice(0, position);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;

  // An @ only opens the picker at a word boundary, so email addresses and
  // already-completed tokens do not reopen it.
  const preceding = at > 0 ? before[at - 1] : ' ';
  if (!/\s|[([]/.test(preceding)) return null;

  const query = before.slice(at + 1);
  if (/[\]()\n]/.test(query)) return null;
  if (query.length > 40) return null;

  return { query, start: at, end: position };
}

export function applyMention(text, range, option) {
  const source = String(text || '');
  const token = mentionToken(option.kind, option.id, option.label);
  const next = `${source.slice(0, range.start)}${token} ${source.slice(range.end)}`;
  return { text: next, caret: range.start + token.length + 1 };
}
